<template>
  <div class="likert-scale-wrapper">
    <el-collapse v-model="active" :accordion="true">
      <el-collapse-item title="Likert Scale Guide" name="guide">
        <p>In this performance review, you’ll answer questions using a 1 to 5 scale, where 1 means the lowest or worst rating, and 5 means the highest or best rating. Each question will ask you to rate specific areas of job performance, like teamwork, communication, or job skills. This helps us consistently measure strengths and identify areas where you might want to grow or get more support</p>
        <div class="likert-container flex">
          <div class="likert-box flex flex-column" v-for="data in guideData" :key="data.id">
            <div class="number">{{ data.id }}</div>
            <div class="text">
              <div class="title">{{ data.title }}</div>
              <div class="description">{{ data.description }}</div>
            </div>
          </div>
        </div>
      </el-collapse-item>
    </el-collapse>
  </div>
</template>

<script>
export default {
  name: 'LikertScale',
  data() {
    return {
      active: ['guide']
    }
  },
  computed: {
    activeSector() {
      return this.$store.getters.activeSector
    },
    user() {
      return this.$store.getters.user
    },
    guideData() {
      if (this.activeSector) {
        if (this.activeSector.id === 1) {
          return [
            {
              id: 1,
              title: 'Strongly disagree',
              description: 'I complete assigned tasks but rarely take initiative or ownership.'
            },
            {
              id: 2,
              title: 'Disagree',
              description: 'I occasionally take initiative but often need guidance.'
            },
            {
              id: 3,
              title: 'Neither agree nor disagree',
              description: 'I regularly take ownership of my tasks and contribute ideas.'
            },
            {
              id: 4,
              title: 'Agree',
              description: 'I frequently take initiative, propose new ideas, and own projects.'
            },
            {
              id: 5,
              title: 'Strongly Agree',
              description: 'I consistently take full ownership, innovate, and pursue new opportunities proactively.'
            }
          ]
        }

        if (this.activeSector.id === 2) {
          return [
            {
              id: 1,
              title: 'Strongly disagree',
              description: 'I sometimes consider feedback or learning opportunities, though I may need extra encouragement to act on them.'
            },
            {
              id: 2,
              title: 'Disagree',
              description: 'I occasionally seek feedback or learning opportunities, but I may hesitate to implement them immediately.'
            },
            {
              id: 3,
              title: 'Neither agree nor disagree',
              description: 'I regularly seek feedback and apply it to improve my performance and knowledge.'
            },
            {
              id: 4,
              title: 'Agree',
              description: 'I frequently seek feedback and actively apply it to enhance my skills and adapt to changes.'
            },
            {
              id: 5,
              title: 'Strongly Agree',
              description: 'I always seek out feedback and learning opportunities, and I consistently use them to improve my performance.'
            }
          ]
        }

        if (this.activeSector.id === 3) {
          return [
            {
              id: 1,
              title: 'Strongly disagree',
              description: 'I sometimes contribute to team efforts, though I may prefer to focus on individual tasks.'
            },
            {
              id: 2,
              title: 'Disagree',
              description: 'I occasionally collaborate with others and offer support, but I may wait for opportunities to arise.'
            },
            {
              id: 3,
              title: 'Neither agree nor disagree',
              description: 'I regularly collaborate with my team and offer support when needed.'
            },
            {
              id: 4,
              title: 'Agree',
              description: 'I frequently take the initiative to collaborate with others and provide support to my colleagues.'
            },
            {
              id: 5,
              title: 'Strongly Agree',
              description: 'I always actively collaborate and offer support to my team, ensuring we achieve our goals together.'
            }
          ]
        }

        if (this.activeSector.id === 4) {
          return [
            {
              id: 1,
              title: 'Strongly disagree',
              description: 'I sometimes take responsibility but struggle to be fully transparent, especially when addressing mistakes.'
            },
            {
              id: 2,
              title: 'Disagree',
              description: 'I occasionally show transparency but may withhold information or avoid difficult topics in challenging situations.'
            },
            {
              id: 3,
              title: 'Neither agree nor disagree',
              description: 'I regularly communicate with transparency while respecting the need for confidentiality in appropriate situations.'
            },
            {
              id: 4,
              title: 'Agree',
              description: 'I frequently make ethical decisions, demonstrating transparency, respect, and maintaining confidentiality under pressure.'
            },
            {
              id: 5,
              title: 'Strongly Agree',
              description: 'I consistently uphold high standards of responsibility, transparency, respect, and confidentiality.'
            }
          ]
        }

        if (this.activeSector.id === 5) {
          return [
            {
              id: 1,
              title: 'Strongly disagree',
              description: 'I sometimes take ownership for my tasks and commitments, but I may miss deadlines or require support to stay on track.'
            },
            {
              id: 2,
              title: 'Disagree',
              description: 'I occasionally take ownership of tasks and meet my commitments, but sometimes need extra time or assistance to ensure completion.'
            },
            {
              id: 3,
              title: 'Neither agree nor disagree',
              description: 'I regularly take ownership of my tasks, meeting commitments on time and ensuring my work is completed to a good standard.'
            },
            {
              id: 4,
              title: 'Agree',
              description: 'I frequently take full ownership of tasks and deliver on commitments, often exceeding expectations by completing work ahead of schedule.'
            },
            {
              id: 5,
              title: 'Strongly Agree',
              description: 'I consistently take full ownership of my responsibilities, delivering high-quality work on time and proactively meeting or exceeding expectations.'
            }
          ]
        }

        if (this.activeSector.id === 6) {
          return [
            {
              id: 1,
              title: 'Strongly disagree',
              description: 'I sometimes support my team, though I may not consistently prioritize guidance or development opportunities.'
            },
            {
              id: 2,
              title: 'Disagree',
              description: 'I occasionally offer direction and development, though I could be more proactive in coaching and feedback.'
            },
            {
              id: 3,
              title: 'Neither agree nor disagree',
              description: 'I regularly guide my team, encouraging growth, collaboration, and effective communication.'
            },
            {
              id: 4,
              title: 'Agree',
              description: 'I frequently provide clear direction and foster a positive, collaborative, and growth-oriented environment.'
            },
            {
              id: 5,
              title: 'Strongly Agree',
              description: 'I consistently lead by example, promoting learning, resolving conflicts, and tailoring support to my team’s needs.'
            }
          ]
        }
      }
    }
  }
}
</script>

<style lang="scss">
  @import "~vars";
  .likert-scale-wrapper {
    background: white;
    width: 100%;
    max-width: 1240px;
    margin: 20px  auto;
    .el-collapse-item__header {
      position: relative;
      padding-left: 20px;
    }
    .el-collapse-item__arrow {
      position: absolute;
      left: 0;
    }
    .likert-container {
      gap: 10px;
      .likert-box {
        background: #F4F4F4;
        border-radius: 8px;
        text-align: center;
        padding: 10px;
        max-width: 20%;
        width: 100%;
        .number {
          font-size: 20px;
          color: #1B2438;
        }
        .text {
          .title {
            font-size: 12px;
            font-weight: 700;
            color: #1B2438;
          }
          .description {
            font-size: 12px;
            color: #1B2438;
            font-weight: 500;
          }
        }
      }
    }
  }
</style>
